import React, { useState, useEffect } from 'react';
import {
    SignedDocument,
    SignedDocumentType,
    DocumentStatistics
} from '../../types/signedDocument';
import { signedDocumentService } from '../../services/signedDocumentService';
import { orderSignatureService, OrderSignature } from '../../services/orderSignatureService';
import {
    DocumentIcon,
    ArrowDownTrayIcon,
    FunnelIcon,
    ChartBarIcon,
    CalendarIcon,
    UserIcon,
    BuildingOfficeIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const DocumentsPage: React.FC = () => {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState<SignedDocument[]>([]);
    const [orderSignatures, setOrderSignatures] = useState<OrderSignature[]>([]);
    const [statistics, setStatistics] = useState<DocumentStatistics | null>(null);
    const [loading, setLoading] = useState(true);
    const [orderSignaturesLoading, setOrderSignaturesLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<SignedDocumentType | 'ALL' | 'SUPPLIER_SIGNED' | 'ORDER_SIGNATURES'>('ALL');
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [downloadingCmsId, setDownloadingCmsId] = useState<number | null>(null);
    const [downloadingOrderCmsId, setDownloadingOrderCmsId] = useState<number | null>(null);

    const loadDocuments = async (type?: SignedDocumentType, contractStatus?: string) => {
        try {
            setLoading(true);
            setError(null);

            const response = await signedDocumentService.getSignedDocuments(type, contractStatus);
            setDocuments(response.documents);
        } catch (err) {
            console.error('Ошибка загрузки документов:', err);
            setError(err instanceof Error ? err.message : 'Ошибка загрузки документов');
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            const stats = await signedDocumentService.getDocumentStatistics();
            setStatistics(stats);
        } catch (err) {
            console.error('Ошибка загрузки статистики:', err);
        }
    };

    const loadOrderSignatures = async () => {
        try {
            setOrderSignaturesLoading(true);
            const signatures = await orderSignatureService.getMyOrderSignatures();
            setOrderSignatures(signatures);
        } catch (err) {
            console.error('Ошибка загрузки заявок:', err);
        } finally {
            setOrderSignaturesLoading(false);
        }
    };

    const handleDownloadOrderSignature = async (orderSignature: OrderSignature) => {
        try {
            await orderSignatureService.downloadOrderSignaturePdf(orderSignature.id, orderSignature.orderId);
        } catch (err) {
            console.error('Ошибка скачивания заявки:', err);
            setError(err instanceof Error ? err.message : 'Ошибка скачивания заявки');
        }
    };

    const handleDownloadOrderCms = async (orderSignature: OrderSignature) => {
        try {
            setDownloadingOrderCmsId(orderSignature.id);
            await orderSignatureService.downloadCmsZip(orderSignature.id, orderSignature.orderId);
        } catch (err) {
            console.error('Ошибка скачивания CMS:', err);
            setError(err instanceof Error ? err.message : 'Ошибка скачивания CMS архива');
        } finally {
            setDownloadingOrderCmsId(null);
        }
    };

    const handleDownload = async (document: SignedDocument) => {
        try {
            setDownloadingId(document.id);
            const fileName = `${document.documentName}.pdf`;
            await signedDocumentService.downloadDocument(document.id, fileName);
        } catch (err) {
            console.error('Ошибка скачивания документа:', err);
            setError(err instanceof Error ? err.message : 'Ошибка скачивания документа');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownloadCms = async (document: SignedDocument) => {
        try {
            if (!document.contractId) {
                setError('Договор не найден для этого документа');
                return;
            }

            setDownloadingCmsId(document.id);
            const contractNumber = document.contractNumber || `contract_${document.contractId}`;
            await signedDocumentService.downloadCmsZip(document.contractId, contractNumber);
        } catch (err) {
            console.error('Ошибка скачивания CMS:', err);
            setError(err instanceof Error ? err.message : 'Ошибка скачивания CMS архива');
        } finally {
            setDownloadingCmsId(null);
        }
    };

    const handleTypeFilter = (type: SignedDocumentType | 'ALL' | 'SUPPLIER_SIGNED' | 'ORDER_SIGNATURES') => {
        setSelectedType(type);
        if (type === 'SUPPLIER_SIGNED') {
            // Фильтр для договоров подписанных поставщиком
            loadDocuments(SignedDocumentType.CONTRACT, 'SUPPLIER_SIGNED');
        } else if (type === 'ORDER_SIGNATURES') {
            // Просто меняем selectedType, отображение заявок управляется отдельно
        } else {
            loadDocuments(type === 'ALL' ? undefined : type, undefined);
        }
    };

    useEffect(() => {
        loadDocuments();
        loadStatistics();
        loadOrderSignatures();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Заголовок */}
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">
                    Подписанные документы
                </h1>

                {statistics && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <ChartBarIcon className="h-4 w-4" />
                        <span>Всего: {statistics.totalDocuments}</span>
                    </div>
                )}
            </div>

            {/* Статистика */}
            {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <DocumentIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-500">Всего документов</p>
                                <p className="text-lg font-semibold text-gray-900">{statistics.totalDocuments}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <DocumentIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-500">Договоры</p>
                                <p className="text-lg font-semibold text-gray-900">{statistics.contractsCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <DocumentIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-500">АВР</p>
                                <p className="text-lg font-semibold text-gray-900">{statistics.avrCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <DocumentIcon className="h-6 w-6 text-orange-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-500">Заявки (архив)</p>
                                <p className="text-lg font-semibold text-gray-900">{statistics.purchaseRequestsCount}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Статистика заявок на подписание */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DocumentIcon className="h-6 w-6 text-teal-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Всего заявок</p>
                            <p className="text-lg font-semibold text-gray-900">{orderSignatures.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DocumentIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Ожидают подписания</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {orderSignatures.filter(s => s.status === 'PENDING_SIGNATURE' || s.status === 'SIGNED_BY_BUYER').length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DocumentIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Подписаны обеими сторонами</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {orderSignatures.filter(s => s.status === 'SIGNED_BY_BOTH').length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DocumentIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Со счётом</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {orderSignatures.filter(s => s.invoicePdfPath).length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DocumentIcon className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">С накладной</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {orderSignatures.filter(s => s.sellingPdfPath).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Фильтры */}
            <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center space-x-2 mb-4">
                    <FunnelIcon className="h-5 w-5 text-gray-400" />
                    <h3 className="text-lg font-medium">Фильтры</h3>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => handleTypeFilter('ALL')}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${selectedType === 'ALL'
                            ? 'bg-blue-100 text-blue-800 border-blue-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } border transition-colors`}
                    >
                        Все документы
                    </button>

                    <button
                        onClick={() => handleTypeFilter(SignedDocumentType.CONTRACT)}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${selectedType === SignedDocumentType.CONTRACT
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } border transition-colors`}
                    >
                        Договоры поставки
                    </button>

                    <button
                        onClick={() => handleTypeFilter(SignedDocumentType.AVR)}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${selectedType === SignedDocumentType.AVR
                            ? 'bg-purple-100 text-purple-800 border-purple-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } border transition-colors`}
                    >
                        АВР
                    </button>

                    <button
                        onClick={() => handleTypeFilter('ORDER_SIGNATURES')}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${selectedType === 'ORDER_SIGNATURES'
                            ? 'bg-orange-100 text-orange-800 border-orange-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } border transition-colors`}
                    >
                        Заявки на покупку ({orderSignatures.length})
                    </button>

                    <button
                        onClick={() => handleTypeFilter('SUPPLIER_SIGNED')}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${selectedType === 'SUPPLIER_SIGNED'
                            ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } border transition-colors`}
                    >
                        Подписанные обеими сторонами
                    </button>

                </div>
            </div>

            {/* Ошибка */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Ошибка</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                            <div className="mt-4">
                                <button
                                    onClick={() => {
                                        if (selectedType === 'SUPPLIER_SIGNED') {
                                            loadDocuments(SignedDocumentType.CONTRACT, 'SUPPLIER_SIGNED');
                                        } else if (selectedType === 'ORDER_SIGNATURES') {
                                            loadDocuments(undefined, undefined);
                                        } else {
                                            loadDocuments(selectedType === 'ALL' ? undefined : selectedType as SignedDocumentType, undefined);
                                        }
                                    }}
                                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                                >
                                    Попробовать снова
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Список заявок на подписание */}
            {selectedType === 'ORDER_SIGNATURES' && (
                <div className="bg-white rounded-lg border">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                            Заявки на подписание ({orderSignatures.length})
                        </h3>
                    </div>

                    {orderSignaturesLoading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        </div>
                    ) : orderSignatures.length === 0 ? (
                        <div className="p-8 text-center">
                            <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-1">Нет заявок</h3>
                            <p className="text-sm text-gray-500">
                                У вас пока нет заявок на подписание
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {orderSignatures.map((signature) => (
                                <div key={signature.id} className="p-6 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-3">
                                                <DocumentIcon className="h-6 w-6 text-teal-600 flex-shrink-0" />
                                                <div className="flex-1">
                                                    <h4 className="text-base font-medium text-gray-900 truncate">
                                                        Заявка № {signature.orderId}
                                                    </h4>
                                                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                                                        <div className="flex items-center space-x-1">
                                                            <CalendarIcon className="h-4 w-4" />
                                                            <span>
                                                                {new Date(signature.createdAt).toLocaleDateString('ru-RU')}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center space-x-1">
                                                            <BuildingOfficeIcon className="h-4 w-4" />
                                                            <span>{signature.companyName}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center flex-wrap gap-2 mt-2">
                                                        <span
                                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                signature.status === 'SIGNED_BY_BOTH'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : signature.status === 'SIGNED_BY_BUYER'
                                                                        ? 'bg-orange-100 text-orange-800'
                                                                        : signature.status === 'PENDING_SIGNATURE'
                                                                            ? 'bg-blue-100 text-blue-800'
                                                                            : 'bg-gray-100 text-gray-800'
                                                            }`}
                                                        >
                                                            {signature.statusDisplay}
                                                        </span>
                                                        
                                                        {signature.isSignedByBuyer && (
                                                            <span className="inline-flex items-center text-xs text-green-600">
                                                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                Покупатель
                                                            </span>
                                                        )}
                                                        
                                                        {signature.isSignedBySupplier && (
                                                            <span className="inline-flex items-center text-xs text-green-600">
                                                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                Поставщик
                                                            </span>
                                                        )}

                                                        {signature.cmsZipFilePath && (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                CMS
                                                            </span>
                                                        )}

                                                        {signature.invoicePdfPath && (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                Счёт
                                                            </span>
                                                        )}

                                                        {signature.sellingPdfPath && (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                                Накладная
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 flex-wrap gap-2">
                                            {signature.generatedPdfPath && (
                                                <button
                                                    onClick={() => handleDownloadOrderSignature(signature)}
                                                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                                    PDF
                                                </button>
                                            )}

                                            {signature.cmsZipFilePath && (
                                                <button
                                                    onClick={() => handleDownloadOrderCms(signature)}
                                                    disabled={downloadingOrderCmsId === signature.id}
                                                    className="inline-flex items-center px-3 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                                                    title="Скачать архив с CMS файлами электронных подписей"
                                                >
                                                    {downloadingOrderCmsId === signature.id ? (
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                                                    ) : (
                                                        <>
                                                            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                                            CMS
                                                        </>
                                                    )}
                                                </button>
                                            )}

                                            {signature.invoicePdfPath && (
                                                <button
                                                    onClick={() => orderSignatureService.downloadInvoice(signature.id, signature.orderId)}
                                                    className="inline-flex items-center px-3 py-2 border border-purple-300 rounded-md text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100"
                                                    title="Скачать счёт на оплату"
                                                >
                                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                                    Счёт
                                                </button>
                                            )}

                                            {signature.sellingPdfPath && (
                                                <button
                                                    onClick={() => orderSignatureService.downloadSelling(signature.id, signature.orderId)}
                                                    className="inline-flex items-center px-3 py-2 border border-indigo-300 rounded-md text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                                                    title="Скачать накладную"
                                                >
                                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                                    Накладная
                                                </button>
                                            )}
                                            
                                            <button
                                                onClick={() => navigate(`/client/orders/${signature.orderId}`)}
                                                className="inline-flex items-center px-3 py-2 border border-blue-600 rounded-md text-sm font-medium text-blue-600 bg-white hover:bg-blue-50"
                                            >
                                                Открыть
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Список документов */}
            {selectedType !== 'ORDER_SIGNATURES' && (
                <div className="bg-white rounded-lg border">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                            Документы ({documents.length})
                        </h3>
                    </div>

                {documents.length === 0 ? (
                    <div className="p-8 text-center">
                        <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-sm font-medium text-gray-900 mb-1">Нет документов</h3>
                        <p className="text-sm text-gray-500">
                            {selectedType === 'ALL'
                                ? 'У вас пока нет подписанных документов'
                                : selectedType === 'SUPPLIER_SIGNED'
                                    ? 'Нет договоров подписанных обеими сторонами'
                                    : `Нет документов типа "${signedDocumentService.getDocumentTypeLabel(selectedType as SignedDocumentType)}"`
                            }
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {documents.map((document) => (
                            <div key={document.id} className="p-6 hover:bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-3">
                                            <DocumentIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                                            <div className="flex-1">
                                                <h4 className="text-base font-medium text-gray-900 truncate">
                                                    {document.documentName}
                                                </h4>
                                                <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                                                    <div className="flex items-center space-x-1">
                                                        <CalendarIcon className="h-4 w-4" />
                                                        <span>
                                                            {signedDocumentService.formatDate(document.signedAt)}
                                                        </span>
                                                    </div>

                                                    {document.signerName && (
                                                        <div className="flex items-center space-x-1">
                                                            <UserIcon className="h-4 w-4" />
                                                            <span>{document.signerName}</span>
                                                        </div>
                                                    )}

                                                    <div className="flex items-center space-x-1">
                                                        <BuildingOfficeIcon className="h-4 w-4" />
                                                        <span>{document.companyName}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center space-x-2 mt-2">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${document.type === SignedDocumentType.CONTRACT
                                                            ? 'bg-green-100 text-green-800'
                                                            : document.type === SignedDocumentType.AVR
                                                                ? 'bg-purple-100 text-purple-800'
                                                                : 'bg-orange-100 text-orange-800'
                                                            }`}
                                                    >
                                                        {signedDocumentService.getDocumentTypeLabel(document.type)}
                                                    </span>

                                                    {document.hasSignature && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            Подписан ЭЦП
                                                        </span>
                                                    )}

                                                    {document.fileSize && (
                                                        <span className="text-xs text-gray-500">
                                                            {signedDocumentService.formatFileSize(document.fileSize)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                                        <button
                                            onClick={() => handleDownload(document)}
                                            disabled={downloadingId === document.id}
                                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                        >
                                            {downloadingId === document.id ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                                            ) : (
                                                <>
                                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                                    Скачать PDF
                                                </>
                                            )}
                                        </button>

                                        {/* Кнопка скачивания CMS - показываем только для договоров с подписями */}
                                        {document.type === SignedDocumentType.CONTRACT && document.hasSignature && document.contractId && (
                                            <button
                                                onClick={() => handleDownloadCms(document)}
                                                disabled={downloadingCmsId === document.id}
                                                className="inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm leading-4 font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                                title="Скачать архив с CMS файлами электронных подписей"
                                            >
                                                {downloadingCmsId === document.id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                                                ) : (
                                                    <>
                                                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                                        Скачать CMS
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                </div>
            )}
        </div>
    );
};

export default DocumentsPage;
